import React, { useEffect, useState } from 'react';
import { Category } from '../types/contractor';
import { Province, Town, fetchTowns } from '../api/locations';

interface ServicesCategoriesEditableProps {
  categories: Category[];
  selectedCategoryIds: number[];
  onSelectedCategoryIdsChange: (ids: number[]) => void;
  serviceAreaTownIds: number[];
  onServiceAreaTownIdsChange: (ids: number[]) => void;
  provinces: Province[];
  townNames: Record<number, string>;
  onTownNamesUpdate: (updates: Record<number, string>) => void;
}

const ServicesCategoriesEditable: React.FC<ServicesCategoriesEditableProps> = ({
  categories,
  selectedCategoryIds,
  onSelectedCategoryIdsChange,
  serviceAreaTownIds,
  onServiceAreaTownIdsChange,
  provinces,
  townNames,
  onTownNamesUpdate,
}) => {
  const [saProvinceId, setSaProvinceId] = useState<number | null>(null);
  const [saTowns, setSaTowns] = useState<Town[]>([]);
  const [saSelectedTownId, setSaSelectedTownId] = useState<number | null>(null);

  useEffect(() => {
    if (saProvinceId) {
      fetchTowns(saProvinceId).then(setSaTowns).catch(() => setSaTowns([]));
      setSaSelectedTownId(null);
    } else {
      setSaTowns([]);
    }
  }, [saProvinceId]);

  const toggleCategory = (id: number) => {
    const next = selectedCategoryIds.includes(id)
      ? selectedCategoryIds.filter(c => c !== id)
      : [...selectedCategoryIds, id];
    onSelectedCategoryIdsChange(next);
  };

  const handleAddServiceArea = () => {
    if (!saSelectedTownId) return;
    if (serviceAreaTownIds.includes(saSelectedTownId)) return;
    const town = saTowns.find(t => t.id === saSelectedTownId);
    if (town) onTownNamesUpdate({ ...townNames, [town.id]: town.name });
    onServiceAreaTownIdsChange([...serviceAreaTownIds, saSelectedTownId]);
    setSaSelectedTownId(null);
  };

  const handleRemoveServiceArea = (townId: number) => {
    onServiceAreaTownIdsChange(serviceAreaTownIds.filter(id => id !== townId));
  };

  return (
    <section className="mb-8 border-b pb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Services &amp; Expertise</h2>

      {/* Categories */}
      <div className="mb-6">
        <h3 className="text-base font-semibold text-gray-700 mb-3">Service Categories</h3>
        <div className="flex flex-wrap gap-2">
          {categories.map(c => {
            const selected = selectedCategoryIds.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCategory(c.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  selected
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                }`}
              >
                {selected && <span className="mr-1.5">✓</span>}
                {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Service Areas */}
      <div>
        <h3 className="text-base font-semibold text-gray-700 mb-3">Service Areas</h3>
        {serviceAreaTownIds.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-3">
            {serviceAreaTownIds.map(id => (
              <span key={id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                {townNames[id] ?? `Town #${id}`}
                <button
                  type="button"
                  onClick={() => handleRemoveServiceArea(id)}
                  className="ml-1 text-blue-600 hover:text-blue-900 font-bold leading-none"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 mb-3">No service areas added yet.</p>
        )}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
            <select
              className="p-2 border border-gray-300 rounded-md min-w-[180px]"
              value={saProvinceId ?? ''}
              onChange={e => setSaProvinceId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Select province</option>
              {provinces.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Town</label>
            <select
              className="p-2 border border-gray-300 rounded-md min-w-[180px]"
              value={saSelectedTownId ?? ''}
              onChange={e => setSaSelectedTownId(e.target.value ? Number(e.target.value) : null)}
              disabled={!saProvinceId}
            >
              <option value="">Select town</option>
              {saTowns.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleAddServiceArea}
            disabled={!saSelectedTownId}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesCategoriesEditable;
